export type CookieYesCategory = 'necessary' | 'functional' | 'analytics' | 'performance' | 'advertisement';
export type CookieYesCategories = Partial<Record<CookieYesCategory, boolean>>;

export type CookieYesConsentState = {
	activeLaw?: string;
	categories: CookieYesCategories;
	isUserActionCompleted?: boolean;
	consentID?: string;
	languageCode?: string;
};

export type CookieYesConsentUpdate = {
	state: CookieYesConsentState;
	accepted: Array<string>;
	rejected: Array<string>;
};

type CookieYesConsentListeners = {
	onRead?: (state: CookieYesConsentState) => void;
	onBannerLoad?: (state: CookieYesConsentState) => void;
	onConsentUpdate?: (update: CookieYesConsentUpdate) => void;
};

type CookieYesEvents = {
	// https://www.cookieyes.com/documentation/events-on-cookie-banner-interactions/
	cookieyes_consent_update: CustomEvent<{ accepted: Array<CookieYesCategory>; rejected: Array<CookieYesCategory> }>;
	// https://www.cookieyes.com/documentation/events-on-cookie-banner-load/
	cookieyes_banner_load: CustomEvent<{
		activeLaw: string;
		categories: CookieYesCategories;
		isUserActionCompleted: boolean;
		consentID?: string;
		languageCode: string;
	}>;
};

export function listenForCookieYesConsentUpdates(listeners: CookieYesConsentListeners) {
	let current: CookieYesConsentState | undefined;

	const readCookieYesConsent = () => {
		const consent = getCookieYesConsent();
		if (!consent) {
			return;
		}

		current = mergeCookieYesConsent(current, consent);
		if (current) {
			listeners.onRead?.(current);
		}
	};

	const onBannerLoad = (event: Event) => {
		// Analytics used this event before the consent-sync work to seed PostHog person properties.
		// Keep forwarding it so the backend sync can ignore it without changing analytics behavior.
		current = mergeCookieYesConsent(current, (event as CookieYesEvents['cookieyes_banner_load']).detail);
		if (current) {
			listeners.onBannerLoad?.(current);
		}
	};

	const onConsentUpdate = (event: Event) => {
		// This event carries category deltas, not a guaranteed full consent snapshot.
		const { accepted, rejected } = (event as CookieYesEvents['cookieyes_consent_update']).detail;
		const categories = { ...(current?.categories ?? {}) };

		for (const category of accepted) {
			categories[category] = true;
		}
		for (const category of rejected) {
			categories[category] = false;
		}

		current = mergeCookieYesConsent(current, {
			...getCookieYesConsent(),
			categories,
		});
		if (current) {
			listeners.onConsentUpdate?.({ state: current, accepted, rejected });
		}

		readCookieYesConsent();
	};

	readCookieYesConsent();

	document.addEventListener('cookieyes_banner_load', onBannerLoad);
	document.addEventListener('cookieyes_consent_update', onConsentUpdate);

	return () => {
		document.removeEventListener('cookieyes_banner_load', onBannerLoad);
		document.removeEventListener('cookieyes_consent_update', onConsentUpdate);
	};
}

export function getCookieYesConsent() {
	return window.getCkyConsent?.();
}

export function mergeCookieYesConsent(
	current: CookieYesConsentState | undefined,
	update: CookieYesConsentState | undefined,
) {
	// CookieYes events are partial across different hooks; keep prior metadata while applying latest categories.
	if (!current) {
		return update;
	}

	if (!update) {
		return current;
	}

	return {
		activeLaw: update.activeLaw ?? current.activeLaw,
		categories: {
			...current.categories,
			...update.categories,
		},
		isUserActionCompleted: update.isUserActionCompleted ?? current.isUserActionCompleted,
		consentID: update.consentID ?? current.consentID,
		languageCode: update.languageCode ?? current.languageCode,
	};
}

declare global {
	interface Document {
		addEventListener<K extends keyof CookieYesEvents>(
			type: K,
			listener: (this: Document, ev: CookieYesEvents[K]) => void,
		): void;
	}

	interface Window {
		getCkyConsent?: () => CookieYesConsentState | undefined;
	}
}
